import { Component, ErrorInfo, ReactNode } from "react";
import { Toast, ErrorMessages } from "@/lib/utils";

interface ErrorBoundaryProps {
	children: ReactNode;
	fallback?: ReactNode;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

/**
 * React Error Boundary để bắt lỗi trong component tree
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		// Update state để next render sẽ hiển thị fallback UI
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		// Log error để debug
		console.error("ErrorBoundary caught an error:", error, errorInfo);

		// Show toast notification
		Toast.error("Đã xảy ra lỗi không mong đợi", "Vui lòng làm mới trang hoặc thử lại sau");

		// Optional: Send error to error reporting service
		// reportError(error, errorInfo);
	}

	render(): ReactNode {
		if (this.state.hasError) {
			// Fallback UI
			return (
				this.props.fallback || (
					<div className="flex items-center justify-center min-h-screen bg-background">
						<div className="text-center p-8 bg-card rounded-lg shadow-md max-w-md">
							<div className="mb-4">
								<svg
									className="w-16 h-16 mx-auto text-red-500 dark:text-red-400"
									fill="none"
									strokeWidth={1.5}
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
									/>
								</svg>
							</div>
							<h2 className="text-xl font-semibold text-foreground mb-2">
								Đã xảy ra lỗi không mong đợi
							</h2>
							<p className="text-muted-foreground mb-6">Vui lòng làm mới trang hoặc thử lại sau</p>
							<button
								onClick={() => window.location.reload()}
								className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium"
							>
								Làm mới trang
							</button>
						</div>
					</div>
				)
			);
		}

		return this.props.children;
	}
}

/**
 * Global Error Handler để setup các event listener cho uncaught errors
 */
export class GlobalErrorHandler {
	private static instance: GlobalErrorHandler;

	private constructor() {
		this.setupGlobalErrorHandlers();
	}

	static getInstance(): GlobalErrorHandler {
		if (!GlobalErrorHandler.instance) {
			GlobalErrorHandler.instance = new GlobalErrorHandler();
		}
		return GlobalErrorHandler.instance;
	}

	private setupGlobalErrorHandlers(): void {
		// Handle unhandled JavaScript errors
		window.addEventListener("error", this.handleGlobalError.bind(this));

		// Handle unhandled promise rejections
		window.addEventListener("unhandledrejection", this.handleUnhandledRejection.bind(this));

		// Handle resource loading errors (images, scripts, etc.)
		window.addEventListener("error", this.handleResourceError.bind(this), true);
	}

	/**
	 * Check if error is related to Zalo
	 */
	private isZaloRelatedError(errorMessage: string): boolean {
		const lowerMessage = errorMessage.toLowerCase();
		return lowerMessage.includes("zalo");
	}

	private handleGlobalError(event: ErrorEvent): void {
		console.error("Global error:", event.error);

		// Extract meaningful error information
		const errorMessage = event.error?.message || event.message || ErrorMessages.UNKNOWN_ERROR;

		// Skip toast for Zalo-related errors, but keep logging
		if (this.isZaloRelatedError(errorMessage)) {
			console.error("Zalo-related error (toast skipped):", errorMessage);
			return;
		}

		// The raw message used to go straight to the screen, which is how a
		// reception desk ended up reading "JSON Parse error: Unterminated string"
		// off a JavaScriptCore internal and asking what they had done wrong. An
		// uncaught error is never actionable by the person at the counter, so tell
		// them what to do instead and keep the detail in the console above, where
		// it is still there for whoever is debugging.
		Toast.error(
			ErrorMessages.UNKNOWN_ERROR,
			"Vui lòng tải lại trang. Nếu còn lỗi, báo quản trị viên.",
		);
	}

	private handleUnhandledRejection(event: PromiseRejectionEvent): void {
		console.error("Unhandled promise rejection:", event.reason);

		// Extract error message from rejection reason
		let errorMessage = ErrorMessages.UNKNOWN_ERROR;

		if (event.reason instanceof Error) {
			errorMessage = event.reason.message;
		} else if (typeof event.reason === "string") {
			errorMessage = event.reason;
		} else if (event.reason && typeof event.reason.message === "string") {
			errorMessage = event.reason.message;
		}

		// Skip toast for Zalo-related errors, but keep logging
		if (this.isZaloRelatedError(errorMessage)) {
			console.error("Zalo-related promise rejection (toast skipped):", errorMessage);
			event.preventDefault();
			return;
		}

		// Check for common error types
		if (
			errorMessage.toLowerCase().includes("network") ||
			errorMessage.toLowerCase().includes("fetch")
		) {
			Toast.error(ErrorMessages.NETWORK_ERROR);
		} else if (errorMessage.toLowerCase().includes("auth")) {
			Toast.error(ErrorMessages.AUTH_REQUIRED);
		} else {
			// Same reasoning as `handleGlobalError`: an unhandled rejection that
			// reached here was not classified by any caller, so its message is a
			// developer's string, not an instruction anyone at a desk can follow.
			Toast.error(
				ErrorMessages.UNKNOWN_ERROR,
				"Vui lòng tải lại trang. Nếu còn lỗi, báo quản trị viên.",
			);
		}

		// Prevent the default browser behavior (console error)
		event.preventDefault();
	}

	private handleResourceError(event: Event): void {
		const target = event.target as HTMLElement;

		// Only handle resource loading errors, not JavaScript errors
		if (target && target.tagName) {
			console.error("Resource loading error:", target.tagName, target);

			const resourceType = target.tagName.toLowerCase();
			let message = "Không thể tải tài nguyên";

			switch (resourceType) {
				case "img":
					message = "Không thể tải hình ảnh";
					break;
				case "script":
					message = "Không thể tải script";
					break;
				case "link":
					message = "Không thể tải stylesheet";
					break;
			}

			Toast.error(message, "Vui lòng kiểm tra kết nối mạng");
		}
	}

	/**
	 * Method to manually report errors
	 */
	static reportError(error: Error, context?: string): void {
		console.error("Manual error report:", error, context);

		const errorMessage = error.message || ErrorMessages.UNKNOWN_ERROR;
		const instance = GlobalErrorHandler.getInstance();

		// Skip toast for Zalo-related errors, but keep logging
		if (instance.isZaloRelatedError(errorMessage)) {
			console.error("Zalo-related manual error (toast skipped):", errorMessage, context);
			return;
		}

		Toast.error(
			context ? `Lỗi ${context}` : "Đã xảy ra lỗi",
			`${errorMessage.slice(0, 100)}${errorMessage.length > 100 ? "..." : ""}`,
		);
	}
}

/**
 * Hook để sử dụng global error handler
 */
export const useGlobalErrorHandler = () => {
	return {
		reportError: GlobalErrorHandler.reportError,
	};
};
